/**
 * Dashboard Settings Page
 * Allows editing dashboard configuration
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Trash2,
  RefreshCw,
  Settings,
  AlertCircle,
} from 'lucide-react';
import { Layout } from '../components/layout';
import { dashboardsApi } from '../api/client';
import { Dashboard } from '../types';

export function DashboardSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    auto_refresh: false,
    refresh_interval_seconds: 60,
    is_public: false,
  });
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Fetch dashboard
  const { data: dashboard, isLoading, error } = useQuery({
    queryKey: ['dashboard', id],
    queryFn: () => dashboardsApi.get(id!),
    enabled: !!id,
  });

  // Update form when dashboard loads
  useEffect(() => {
    if (dashboard) {
      setFormData({
        name: dashboard.name,
        description: dashboard.description || '',
        auto_refresh: dashboard.auto_refresh,
        refresh_interval_seconds: dashboard.refresh_interval_seconds,
        is_public: dashboard.is_public,
      });
    }
  }, [dashboard]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Dashboard>) => dashboardsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => dashboardsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      navigate('/dashboards');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate();
    } else {
      setDeleteConfirm(true);
    }
  };

  if (isLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-[#45bdd6] animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading dashboard settings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !dashboard) {
    return (
      <Layout title="Error">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-500 mb-4">
              {error?.toString() || 'Dashboard not found'}
            </p>
            <Link
              to="/dashboards"
              className="text-[#45bdd6] hover:underline flex items-center gap-2 justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboards
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Settings: ${dashboard.name}`}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <Link
              to={`/dashboards/${id}`}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </Link>
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-[#45bdd6]" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Dashboard Settings
                </h1>
                <p className="text-sm text-gray-500">{dashboard.name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50">
          <div className="max-w-2xl mx-auto">
            {/* Success Message */}
            {updateMutation.isSuccess && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                Settings saved successfully!
              </div>
            )}

            {/* Error Message */}
            {updateMutation.isError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                Failed to save settings. Please try again.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  General
                </h2>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                      placeholder="Dashboard name"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                      rows={3}
                      placeholder="Optional description"
                    />
                  </div>
                </div>
              </div>

              {/* Refresh Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Auto Refresh
                </h2>

                <div className="space-y-4">
                  {/* Auto Refresh Toggle */}
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.auto_refresh}
                      onChange={(e) => setFormData({ ...formData, auto_refresh: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-[#45bdd6] focus:ring-[#45bdd6]"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Enable auto refresh
                      </span>
                      <p className="text-xs text-gray-500">
                        Automatically refresh widget data at regular intervals
                      </p>
                    </div>
                  </label>

                  {/* Refresh Interval */}
                  {formData.auto_refresh && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Refresh Interval (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.refresh_interval_seconds}
                        onChange={(e) => setFormData({
                          ...formData,
                          refresh_interval_seconds: parseInt(e.target.value) || 60
                        })}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                        min={10}
                        max={3600}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum: 10 seconds, Maximum: 3600 seconds (1 hour)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Visibility Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Visibility
                </h2>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-[#45bdd6] focus:ring-[#45bdd6]"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Public dashboard
                    </span>
                    <p className="text-xs text-gray-500">
                      Allow other users to view this dashboard
                    </p>
                  </div>
                </label>
              </div>

              {/* Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Information
                </h2>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(dashboard.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Updated:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(dashboard.updated_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Widgets:</span>
                    <span className="ml-2 text-gray-900">{dashboard.widget_count}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">ID:</span>
                    <span className="ml-2 text-gray-900 font-mono text-xs">{dashboard.id}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50"
                >
                  {updateMutation.isPending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
                </button>

                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    deleteConfirm
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  {deleteMutation.isPending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {deleteConfirm ? 'Click again to confirm' : 'Delete Dashboard'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}
